import { defineStore } from 'pinia'
import { ref } from 'vue'
import {
  CARD_FOIL_IDS_BEST_FIRST,
  acquiredAtMs as copiesAcquiredAtMs,
  countsFromCopies,
  deckIsOwned as copiesOwned,
  emptyPackCounts,
  foilCount as copiesFoilCount,
  ownedCount as copiesOwnedCount,
  type AcquiredAtMap,
  type CardFoilId,
  type CollectionState,
  type CopiesMap,
  type RecycleLine,
  type SavedDeckSlot,
  type FoilCounts,
  type PackId,
  type DailyMissionsSnapshot,
} from '@shared/index'
import { api, ApiError } from '../api/http'
import { useAuthStore } from './auth'
import { useMissionsStore } from './missions'
import { toPulls, type PackPull } from '../game/packs'

export type { FoilCounts }

function emptyCollection(): CollectionState {
  return {
    copies: {},
    counts: {},
    packs: emptyPackCounts(),
    preferredFoil: {},
    acquiredAt: {},
    welcomePending: false,
  }
}

export const useCollectionStore = defineStore('collection', () => {
  const welcomePending = ref(false)
  const counts = ref<Record<string, number>>({})
  const copies = ref<CopiesMap>({})
  const album = ref<CopiesMap>({})
  const preferredFoil = ref<Record<string, CardFoilId>>({})
  const acquiredAt = ref<AcquiredAtMap>({})
  const packs = ref<Record<PackId, number>>(emptyPackCounts())
  const busy = ref(false)
  /** True while the pack veil or the reveal is on screen. */
  const packCeremony = ref(false)
  /** Bumps when a collection write is sent. Older replies must not apply. */
  let mutationEpoch = 0

  function applyState(state: CollectionState): void {
    copies.value = state.copies
    album.value = state.album ?? {}
    counts.value = state.counts
    packs.value = state.packs
    preferredFoil.value = state.preferredFoil
    acquiredAt.value = state.acquiredAt ?? {}
    welcomePending.value = state.welcomePending
  }

  function readMutationEpoch(): number {
    return mutationEpoch
  }

  function beginMutation(): number {
    mutationEpoch += 1
    return mutationEpoch
  }

  /** Applies a server snapshot only when no later write has been sent. */
  function applyIfCurrent(epoch: number, state: CollectionState): boolean {
    if (epoch !== mutationEpoch) return false
    applyState(state)
    return true
  }

  function reset(): void {
    mutationEpoch += 1
    applyState(emptyCollection())
    busy.value = false
    packCeremony.value = false
  }

  function setPackCeremony(active: boolean): void {
    packCeremony.value = active
  }

  function foilCount(cardId: string | number, foil: CardFoilId): number {
    return copiesFoilCount(copies.value, cardId, foil)
  }

  function ownedCount(cardId: string | number): number {
    return copiesOwnedCount(copies.value, cardId)
  }

  function albumFoils(cardId: string): CardFoilId[] {
    const row = album.value[cardId]
    if (!row) return []
    return CARD_FOIL_IDS_BEST_FIRST.filter((foil) => (row[foil] ?? 0) > 0)
  }

  function seen(cardId: string | number): boolean {
    const id = String(cardId)
    return ownedCount(id) > 0 || albumFoils(id).length > 0
  }

  /**
   * Last time this card gained a copy, or 0 when the date is unknown.
   */
  function acquiredAtMs(cardId: string | number): number {
    return copiesAcquiredAtMs(acquiredAt.value, cardId)
  }

  /**
   * Distinct owned foils for this card, best rarity first.
   */
  function ownedFoils(cardId: string | number): CardFoilId[] {
    const id = String(cardId)
    return CARD_FOIL_IDS_BEST_FIRST.filter((foil) => foilCount(id, foil) > 0)
  }

  /**
   * Foil shown in the album / trunk. Preferred if still owned, else the best
   * owned print.
   */
  function displayFoil(cardId: string | number): CardFoilId {
    const id = String(cardId)
    const owned = ownedFoils(id)
    const shown = owned.length > 0 ? owned : albumFoils(id)
    if (shown.length === 0) return 'common'
    const preferred = preferredFoil.value[id]
    if (preferred && shown.includes(preferred)) return preferred
    return shown[0] ?? 'common'
  }

  function token(): string | null {
    const value = useAuthStore().token
    return value || null
  }

  /**
   * Next unused print for a new deck copy. Prefers the album choice, then
   * the best remaining foil.
   */
  function nextAvailableFoil(
    cardId: string | number,
    used: FoilCounts,
  ): CardFoilId | null {
    const id = String(cardId)
    const remaining = (foil: CardFoilId): number =>
      foilCount(id, foil) - (used[foil] ?? 0)
    const preferred = displayFoil(id)
    if (remaining(preferred) > 0) return preferred
    for (const foil of CARD_FOIL_IDS_BEST_FIRST) {
      if (remaining(foil) > 0) return foil
    }
    return null
  }

  /**
   * Saves the album display foil on the account.
   */
  async function setPreferredFoil(
    cardId: string | number,
    foil: CardFoilId,
  ): Promise<void> {
    const id = String(cardId)
    if (foilCount(id, foil) < 1) return
    preferredFoil.value = { ...preferredFoil.value, [id]: foil }
    const auth = token()
    if (!auth) return
    const epoch = beginMutation()
    try {
      const payload = await api<{ collection: CollectionState }>(
        '/collection/preferred-foil',
        {
          method: 'PATCH',
          token: auth,
          body: JSON.stringify({ cardId: id, foil }),
        },
      )
      applyIfCurrent(epoch, payload.collection)
    } catch (err) {
      if (err instanceof ApiError && err.status === 401) return
    }
  }

  /**
   * Closes the first-login overlay on the account.
   */
  async function dismissWelcome(): Promise<void> {
    welcomePending.value = false
    const auth = token()
    if (!auth) return
    const epoch = beginMutation()
    try {
      const payload = await api<{ collection: CollectionState }>(
        '/collection/welcome-dismiss',
        { method: 'POST', token: auth },
      )
      applyIfCurrent(epoch, payload.collection)
    } catch {
      /* overlay already closed locally */
    }
  }

  /**
   * Recycles extra prints. Applies the album when this is still the latest write.
   */
  async function recycle(lines: RecycleLine[]): Promise<{
    coins: number
    delta: number
    decks: SavedDeckSlot[]
    activeDeckId: string | null
    deckWrittenAt: number
  } | null> {
    const auth = token()
    if (!auth || lines.length < 1 || busy.value) return null
    busy.value = true
    const epoch = beginMutation()
    try {
      const payload = await api<{
        coins: number
        delta: number
        collection: CollectionState
        decks: SavedDeckSlot[]
        activeDeckId: string | null
        deckWrittenAt: number
      }>('/collection/recycle', {
        method: 'POST',
        token: auth,
        body: JSON.stringify({ lines }),
        signal: AbortSignal.timeout(20_000),
      })
      applyIfCurrent(epoch, payload.collection)
      return payload
    } catch (err) {
      if (err instanceof ApiError && err.code === 'deck_locked') throw err
      return null
    } finally {
      busy.value = false
    }
  }

  /**
   * Opens `qty` packs of the same type on the server.
   */
  async function openPacks(
    packId: PackId,
    qty: number,
  ): Promise<PackPull[][] | null> {
    const auth = token()
    if (!auth || qty < 1 || (packs.value[packId] ?? 0) < qty || busy.value) {
      return null
    }
    busy.value = true
    const epoch = beginMutation()
    try {
      const payload = await api<{
        pulls: { cardId: string; foil: CardFoilId; isNew: boolean }[][]
        collection: CollectionState
        missions?: DailyMissionsSnapshot
      }>('/collection/open', {
        method: 'POST',
        token: auth,
        body: JSON.stringify({ packId, qty }),
      })
      applyIfCurrent(epoch, payload.collection)
      if (payload.missions) {
        useMissionsStore().apply(payload.missions)
        if (typeof payload.missions.coins === 'number') {
          const { useWalletStore } = await import('./wallet')
          useWalletStore().setCoins(payload.missions.coins)
        }
      }
      return payload.pulls.map((batch) => toPulls(batch))
    } catch {
      return null
    } finally {
      busy.value = false
    }
  }

  /**
   * DevTools: 3 copies of every house foil. Needs the DevTools password.
   */
  async function grantAllPrints(password: string): Promise<boolean> {
    const auth = token()
    if (!auth) return false
    const epoch = beginMutation()
    try {
      const payload = await api<{ collection: CollectionState }>(
        '/collection/dev-grant',
        {
          method: 'POST',
          token: auth,
          body: JSON.stringify({ password }),
        },
      )
      applyIfCurrent(epoch, payload.collection)
      return true
    } catch {
      return false
    }
  }

  /**
   * DevTools: add unopened shop boosters. Needs the DevTools password.
   */
  async function grantShopPacks(
    qty: number,
    password: string,
  ): Promise<boolean> {
    const auth = token()
    if (!auth) return false
    const epoch = beginMutation()
    try {
      const payload = await api<{ collection: CollectionState }>(
        '/collection/dev-grant-packs',
        {
          method: 'POST',
          token: auth,
          body: JSON.stringify({ qty, password }),
        },
      )
      applyIfCurrent(epoch, payload.collection)
      return true
    } catch {
      return false
    }
  }

  function applyPacks(next: Record<PackId, number>): void {
    packs.value = next
    counts.value = countsFromCopies(copies.value)
  }

  function deckIsOwned(
    main: string[],
    extra: string[],
    mainFoils?: string[],
    extraFoils?: string[],
  ): boolean {
    return copiesOwned(copies.value, main, extra, mainFoils, extraFoils)
  }

  return {
    welcomePending,
    counts,
    copies,
    album,
    preferredFoil,
    acquiredAt,
    packs,
    busy,
    packCeremony,
    setPackCeremony,
    acquiredAtMs,
    applyState,
    applyPacks,
    mutationEpoch: readMutationEpoch,
    reset,
    ownedCount,
    seen,
    foilCount,
    ownedFoils,
    displayFoil,
    setPreferredFoil,
    nextAvailableFoil,
    dismissWelcome,
    recycle,
    grantAllPrints,
    grantShopPacks,
    openPacks,
    deckIsOwned,
  }
})
